import { FC } from "react";
import { TimelineConnector, TimelineContent, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Co2Icon, FlagIcon, PinDropIcon } from "../../../../../../../../assets/icons";
import { formatNumber } from "../../../../../../../../common/utils";
import { tripNodeIcon } from "../../../../../../../../components/charts/GraphMap/utils";
import { MeasurableValueDTO, NodesPageItemDTO } from "../../../../../../../../datasources/api-client/data-contracts";
import { ThemeColors } from "../../../../../../../../theme/themeValues";

interface Props {
	node: NodesPageItemDTO;
	nodeType: "start" | "end" | "transit";
	hubEmission?: MeasurableValueDTO;
}

/**
 * Renders the details about a single node of a trip
 * MUST be used inside a Timeline
 * @param node node to be displayed
 * @param nodeType either the node is the starting node, the last node or just a transit node
 * @returns
 */
const NodeDetails: FC<Props> = ({ node, nodeType, hubEmission }) => {
	const { t } = useTranslation("result.transport");
	const icon = tripNodeIcon(node.typeId, ThemeColors.LightAction, 20);

	// calculate various styling options based on the node type

	// calculating icon color (atm green for starting nodes, red for ending nodes and gray for transit nodes)
	const iconColor =
		nodeType === "start"
			? ThemeColors.LightWarning
			: nodeType === "end"
				? ThemeColors.LightSuccess
				: ThemeColors.LightAction;

	// calculating extra icon (flag icon only present on first and last NodeDetails)
	const extraIcon =
		nodeType === "start" || nodeType === "end" ? <FlagIcon fontSize="large" sx={{ color: iconColor }} /> : <></>;

	// calculating timeline connector offset (vertical)
	// used to fix the lenght of the timeline to our desired style
	const timelineConnectorOffset = nodeType === "start" ? "54px" : nodeType === "end" ? "-100%" : "";

	// creating an "sx" object for the timelineConnector
	const timelineConnectorSX =
		nodeType === "start"
			? { position: "relative", top: timelineConnectorOffset, zIndex: 1 }
			: nodeType === "end"
				? { position: "relative", top: timelineConnectorOffset, zIndex: -1 }
				: {};

	// setting up the heading of the NodeDetails
	const heading = (
		<Stack direction="row" spacing={1} alignItems={"center"}>
			{extraIcon}
			{icon}
			<Typography variant="subtitle2" color="textPrimary">
				{node.typeDescription}
			</Typography>
		</Stack>
	);

	// rendering the component inside a timeline item
	return (
		<TimelineItem>
			<TimelineSeparator>
				<TimelineConnector sx={timelineConnectorSX} />
			</TimelineSeparator>
			<TimelineContent position="relative" left="-28px" padding="0 !important" minWidth="100%">
				<Paper elevation={3} sx={{ padding: 2, width: "100%", marginBottom: 2 }}>
					<Stack direction="column" spacing={1}>
						{heading}
						<Stack paddingLeft={5} spacing={1}>
							<Typography variant="h6" color="textPrimary">
								{node.code} {node.code && node.description ? " - " : ""} {node.description}
							</Typography>
							<Stack direction="row" spacing={1} alignItems={"center"}>
								<PinDropIcon sx={{ color: ThemeColors.LightAction }} fontSize="small" />
								<Typography variant="body1" color="textPrimary">
									{node.city}
									{node.city && (node.address || node.zipCode) ? ", " : ""}
									{node.address}
									{node.address && node.zipCode ? ", " : ""}
									{node.zipCode}
								</Typography>
							</Stack>
							<Stack direction="row" spacing={1} alignItems={"center"}>
								<Co2Icon sx={{ color: ThemeColors.LightAction }} fontSize="small" />
								<Typography variant="body1" color="textPrimary">
									{t("hub emissions")}
								</Typography>
								<Typography variant="body1" fontWeight={700} color="textPrimary">
									{hubEmission?.value ? formatNumber(hubEmission?.value, "decimal", 2, 3) : "0"}
								</Typography>
								<Typography alignSelf={"flex-end"} fontSize={"small"} color={ThemeColors.LightAction}>
									{hubEmission?.measure || "tCO₂e"}
								</Typography>
							</Stack>
						</Stack>
					</Stack>
				</Paper>
			</TimelineContent>
		</TimelineItem>
	);
};

export default NodeDetails;
